import {
  F_SOLID, STATE_FLAGS, TOTAL_STATES, blockOf, faceTexture, getBool, getFacing,
  getInt, getProp, isSolid,
} from './blocks.ts';
import { Dir, type Direction } from './direction.ts';

/**
 * Per-state geometry.
 *
 * Both collision and rendering are expressed as lists of axis-aligned boxes in
 * unit block space. Full cubes never reach this table — they take the fast path
 * in the mesher and the physics code — so everything here is a slab, stair,
 * fence, or one of the fiddly redstone components.
 *
 * Tables are built lazily per state and cached, because most worlds only ever
 * touch a small fraction of the 2664 states.
 */

export interface RenderBox {
  x0: number; y0: number; z0: number;
  x1: number; y1: number; z1: number;
  /** Texture layer per face, indexed by Direction. */
  tex: Uint16Array;
  /**
   * Faces that may be culled when the neighbouring block is opaque. Only set
   * for faces flush with the block boundary.
   */
  cullMask: number;
  /** Faces whose UVs should use the box extent rather than the full face. */
  uvLock: boolean;
}

export type Aabb = readonly [number, number, number, number, number, number];

const FULL_CUBE: Aabb = [0, 0, 0, 1, 1, 1];
const EMPTY: readonly Aabb[] = [];
const FULL: readonly Aabb[] = [FULL_CUBE];

const collisionCache: (readonly Aabb[] | undefined)[] = new Array(TOTAL_STATES);
const renderCache: (readonly RenderBox[] | undefined)[] = new Array(TOTAL_STATES);
const supportCache = new Int8Array(TOTAL_STATES).fill(-1);

export function collisionBoxes(state: number): readonly Aabb[] {
  const cached = collisionCache[state];
  if (cached) return cached;
  const boxes = buildCollision(state);
  collisionCache[state] = boxes;
  return boxes;
}

export function renderBoxes(state: number): readonly RenderBox[] {
  const cached = renderCache[state];
  if (cached) return cached;
  const boxes = buildRender(state);
  renderCache[state] = boxes;
  return boxes;
}

/** True when the block's top face is a full solid square things can sit on. */
export function hasSolidTop(state: number): boolean {
  const cached = supportCache[state]!;
  if (cached >= 0) return cached === 1;
  let result = false;
  if (isSolid(state)) {
    result = true;
  } else {
    for (const b of collisionBoxes(state)) {
      if (b[1] <= 0.001 && b[4] >= 0.999 && b[0] <= 0.001 && b[3] >= 0.999 && b[2] <= 0.001 && b[5] >= 0.999) {
        result = true;
        break;
      }
      if (b[4] >= 0.999 && b[0] <= 0.001 && b[3] >= 0.999 && b[2] <= 0.001 && b[5] >= 0.999) {
        result = true;
        break;
      }
    }
  }
  supportCache[state] = result ? 1 : 0;
  return result;
}

// ---------------------------------------------------------------------------
// Collision
// ---------------------------------------------------------------------------

function buildCollision(state: number): readonly Aabb[] {
  const def = blockOf(state);
  if ((STATE_FLAGS[state]! & F_SOLID) !== 0) return FULL;
  if (!def.collides) return EMPTY;

  const name = def.name;
  if (name.endsWith('_slab')) {
    const type = getProp(state, 'type');
    if (type === 'double') return FULL;
    return type === 'top' ? [[0, 0.5, 0, 1, 1, 1]] : [[0, 0, 0, 1, 0.5, 1]];
  }
  if (name.endsWith('_stairs')) return stairBoxes(state);
  if (name.endsWith('_fence')) return fenceBoxes(state, 0.375, 0.625, 1.5);
  if (name === 'glass_pane' || name === 'iron_bars') return fenceBoxes(state, 0.4375, 0.5625, 1);
  if (name.endsWith('_fence_gate')) {
    return getBool(state, 'open') ? EMPTY : [[0, 0, 0, 1, 1.5, 1]];
  }
  if (name.endsWith('_door')) return doorBoxes(state);
  if (name.endsWith('_trapdoor')) return trapdoorBoxes(state);
  if (name === 'chest' || name === 'trapped_chest') return [[0.0625, 0, 0.0625, 0.9375, 0.875, 0.9375]];
  if (name === 'hopper') return hopperBoxes();
  if (name === 'cactus') return [[0.0625, 0, 0.0625, 0.9375, 1, 0.9375]];
  if (name === 'farmland') return [[0, 0, 0, 1, 0.9375, 1]];
  if (name === 'snow_layer') {
    const layers = getInt(state, 'layers');
    return layers >= 8 ? FULL : layers <= 1 ? EMPTY : [[0, 0, 0, 1, layers / 8, 1]];
  }
  if (name === 'lily_pad') return [[0.0625, 0, 0.0625, 0.9375, 0.015625, 0.9375]];
  if (name === 'ladder') return EMPTY;
  if (name === 'piston' || name === 'sticky_piston') {
    if (!getBool(state, 'extended')) return FULL;
    return [pistonBaseBox(getFacing(state))];
  }
  if (name === 'piston_head') return [pistonHeadBox(getFacing(state))];
  if (name === 'lantern') {
    return getBool(state, 'hanging')
      ? [[0.3125, 0.125, 0.3125, 0.6875, 0.75, 0.6875]]
      : [[0.3125, 0, 0.3125, 0.6875, 0.5625, 0.6875]];
  }
  if (name === 'campfire') return [[0, 0, 0, 1, 0.4375, 1]];
  if (name === 'end_portal_frame') {
    return getBool(state, 'eye')
      ? [[0, 0, 0, 1, 0.8125, 1], [0.25, 0.8125, 0.25, 0.75, 1, 0.75]]
      : [[0, 0, 0, 1, 0.8125, 1]];
  }
  if (name === 'dragon_egg') return [[0.0625, 0, 0.0625, 0.9375, 1, 0.9375]];
  if (name === 'daylight_detector') return [[0, 0, 0, 1, 0.375, 1]];
  if (name === 'chorus_plant') return fenceBoxes(state, 0.1875, 0.8125, 1);
  if (name === 'end_rod') {
    const f = getFacing(state);
    return [thinAxisBox(f, 0.375, 0.625)];
  }
  if (name === 'vine') return EMPTY;
  return EMPTY;
}

function stairBoxes(state: number): readonly Aabb[] {
  const facing = getFacing(state);
  const top = getProp(state, 'half') === 'top';
  const shape = getProp(state, 'shape') ?? 'straight';
  const base: Aabb = top ? [0, 0.5, 0, 1, 1, 1] : [0, 0, 0, 1, 0.5, 1];
  const stepY0 = top ? 0 : 0.5;
  const stepY1 = top ? 0.5 : 1;

  // The step half occupies the side *opposite* the facing direction.
  const half = sideBox(facing, stepY0, stepY1, true);
  if (shape === 'straight') return [base, half];

  // Corner shapes add or remove a quarter of the step.
  const left = rotateLeft(facing);
  const right = rotateRight(facing);
  if (shape === 'outer_left' || shape === 'outer_right') {
    const side = shape === 'outer_left' ? left : right;
    return [base, quarterBox(facing, side, stepY0, stepY1, true)];
  }
  const side = shape === 'inner_left' ? left : right;
  return [base, half, quarterBox(opposite(facing), side, stepY0, stepY1, true)];
}

function sideBox(facing: Direction, y0: number, y1: number, opposite_: boolean): Aabb {
  const d = opposite_ ? opposite(facing) : facing;
  switch (d) {
    case Dir.NORTH: return [0, y0, 0, 1, y1, 0.5];
    case Dir.SOUTH: return [0, y0, 0.5, 1, y1, 1];
    case Dir.WEST: return [0, y0, 0, 0.5, y1, 1];
    default: return [0.5, y0, 0, 1, y1, 1];
  }
}

function quarterBox(a: Direction, b: Direction, y0: number, y1: number, invert: boolean): Aabb {
  const boxA = sideBox(a, y0, y1, invert);
  const boxB = sideBox(b, y0, y1, invert);
  return [
    Math.max(boxA[0], boxB[0]), y0, Math.max(boxA[2], boxB[2]),
    Math.min(boxA[3], boxB[3]), y1, Math.min(boxA[5], boxB[5]),
  ];
}

function opposite(d: Direction): Direction {
  return (d < 2 ? (d ^ 1) : d ^ 1) as Direction;
}
function rotateLeft(d: Direction): Direction {
  switch (d) {
    case Dir.NORTH: return Dir.WEST;
    case Dir.WEST: return Dir.SOUTH;
    case Dir.SOUTH: return Dir.EAST;
    default: return Dir.NORTH;
  }
}
function rotateRight(d: Direction): Direction {
  return rotateLeft(rotateLeft(rotateLeft(d)));
}

function fenceBoxes(state: number, lo: number, hi: number, height: number): readonly Aabb[] {
  const out: Aabb[] = [[lo, 0, lo, hi, height, hi]];
  if (getBool(state, 'north')) out.push([lo, 0, 0, hi, height, lo]);
  if (getBool(state, 'south')) out.push([lo, 0, hi, hi, height, 1]);
  if (getBool(state, 'west')) out.push([0, 0, lo, lo, height, hi]);
  if (getBool(state, 'east')) out.push([hi, 0, lo, 1, height, hi]);
  return out;
}

function doorBoxes(state: number): readonly Aabb[] {
  const facing = getFacing(state);
  const open = getBool(state, 'open');
  const hinge = getProp(state, 'hinge') === 'right';
  // An open door swings 90 degrees toward the hinge side.
  const dir = open ? (hinge ? rotateRight(facing) : rotateLeft(facing)) : facing;
  return [thinFaceBox(dir, 0.1875)];
}

function trapdoorBoxes(state: number): readonly Aabb[] {
  if (getBool(state, 'open')) return [thinFaceBox(getFacing(state), 0.1875)];
  return getProp(state, 'half') === 'top' ? [[0, 0.8125, 0, 1, 1, 1]] : [[0, 0, 0, 1, 0.1875, 1]];
}

/** A thin slab flush against the given face. */
function thinFaceBox(d: Direction, thickness: number): Aabb {
  switch (d) {
    case Dir.NORTH: return [0, 0, 0, 1, 1, thickness];
    case Dir.SOUTH: return [0, 0, 1 - thickness, 1, 1, 1];
    case Dir.WEST: return [0, 0, 0, thickness, 1, 1];
    case Dir.EAST: return [1 - thickness, 0, 0, 1, 1, 1];
    case Dir.DOWN: return [0, 0, 0, 1, thickness, 1];
    default: return [0, 1 - thickness, 0, 1, 1, 1];
  }
}

/** A rod centred on the given axis. */
function thinAxisBox(d: Direction, lo: number, hi: number): Aabb {
  if (d === Dir.UP || d === Dir.DOWN) return [lo, 0, lo, hi, 1, hi];
  if (d === Dir.NORTH || d === Dir.SOUTH) return [lo, lo, 0, hi, hi, 1];
  return [0, lo, lo, 1, hi, hi];
}

function pistonBaseBox(facing: Direction): Aabb {
  switch (facing) {
    case Dir.DOWN: return [0, 0.25, 0, 1, 1, 1];
    case Dir.UP: return [0, 0, 0, 1, 0.75, 1];
    case Dir.NORTH: return [0, 0, 0.25, 1, 1, 1];
    case Dir.SOUTH: return [0, 0, 0, 1, 1, 0.75];
    case Dir.WEST: return [0.25, 0, 0, 1, 1, 1];
    default: return [0, 0, 0, 0.75, 1, 1];
  }
}

function pistonHeadBox(facing: Direction): Aabb {
  switch (facing) {
    case Dir.DOWN: return [0, 0, 0, 1, 0.25, 1];
    case Dir.UP: return [0, 0.75, 0, 1, 1, 1];
    case Dir.NORTH: return [0, 0, 0, 1, 1, 0.25];
    case Dir.SOUTH: return [0, 0, 0.75, 1, 1, 1];
    case Dir.WEST: return [0, 0, 0, 0.25, 1, 1];
    default: return [0.75, 0, 0, 1, 1, 1];
  }
}

function hopperBoxes(): readonly Aabb[] {
  return [
    [0, 0.625, 0, 1, 1, 1], // rim
    [0, 0.4375, 0, 1, 0.625, 1], // body
    [0.25, 0.25, 0.25, 0.75, 0.4375, 0.75], // funnel
  ];
}

// ---------------------------------------------------------------------------
// Render models
// ---------------------------------------------------------------------------

const CULL_ALL = 0b111111;

function boxFrom(state: number, a: Aabb, cull = 0, texOverride?: Partial<Record<Direction, number>>): RenderBox {
  const tex = new Uint16Array(6);
  for (let d = 0; d < 6; d++) tex[d] = texOverride?.[d as Direction] ?? faceTexture(state, d as Direction);
  return {
    x0: a[0], y0: a[1], z0: a[2], x1: a[3], y1: a[4], z1: a[5],
    tex,
    cullMask: cull,
    uvLock: true,
  };
}

/** Which faces of a box sit exactly on the block boundary, so may be culled. */
function autoCull(a: Aabb): number {
  let mask = 0;
  if (a[1] <= 0.0001) mask |= 1 << Dir.DOWN;
  if (a[4] >= 0.9999) mask |= 1 << Dir.UP;
  if (a[2] <= 0.0001) mask |= 1 << Dir.NORTH;
  if (a[5] >= 0.9999) mask |= 1 << Dir.SOUTH;
  if (a[0] <= 0.0001) mask |= 1 << Dir.WEST;
  if (a[3] >= 0.9999) mask |= 1 << Dir.EAST;
  return mask;
}

function buildRender(state: number): readonly RenderBox[] {
  const def = blockOf(state);
  const name = def.name;

  if (name === 'torch' || name === 'redstone_torch' || name === 'soul_torch') {
    return [boxFrom(state, [0.4375, 0, 0.4375, 0.5625, 0.625, 0.5625])];
  }
  if (name === 'wall_torch' || name === 'redstone_wall_torch') {
    const f = getFacing(state);
    const off = 0.3;
    const dx = f === Dir.WEST ? off : f === Dir.EAST ? -off : 0;
    const dz = f === Dir.NORTH ? off : f === Dir.SOUTH ? -off : 0;
    return [boxFrom(state, [0.4375 + dx, 0.2, 0.4375 + dz, 0.5625 + dx, 0.825, 0.5625 + dz])];
  }
  if (name === 'lever') {
    return [
      boxFrom(state, [0.3125, 0, 0.3125, 0.6875, 0.1875, 0.6875]),
      boxFrom(state, [0.4375, 0.1875, 0.4375, 0.5625, 0.625, 0.5625]),
    ];
  }
  if (name.endsWith('_button')) {
    const powered = getBool(state, 'powered');
    const h = powered ? 0.0625 : 0.125;
    return [boxFrom(state, [0.3125, 0, 0.375, 0.6875, h, 0.625])];
  }
  if (name.endsWith('_pressure_plate')) {
    const h = getBool(state, 'powered') ? 0.03125 : 0.0625;
    return [boxFrom(state, [0.0625, 0, 0.0625, 0.9375, h, 0.9375], 1 << Dir.DOWN)];
  }
  if (name === 'repeater' || name === 'comparator') {
    const boxes: RenderBox[] = [boxFrom(state, [0, 0, 0, 1, 0.125, 1], 1 << Dir.DOWN)];
    const facing = getFacing(state);
    const lit = getBool(state, 'powered');
    const torchTex = faceTexture(lit ? state : state, Dir.UP);
    // Two little torches whose spacing encodes delay / comparator mode.
    const offsets = name === 'repeater'
      ? [-0.3125, -0.3125 + (getInt(state, 'delay') - 1) * 0.125 + 0.125]
      : [-0.3125, 0.3125];
    for (const o of offsets) {
      const [ox, oz] = alongFacing(facing, o);
      boxes.push(boxFrom(state, [0.4375 + ox, 0.125, 0.4375 + oz, 0.5625 + ox, 0.375, 0.5625 + oz], 0, {
        [Dir.UP]: torchTex,
      }));
    }
    if (name === 'comparator') {
      const [sx, sz] = alongFacing(facing, 0.375);
      boxes.push(boxFrom(state, [0.375 + sx, 0.125, 0.375 + sz, 0.625 + sx, 0.3125, 0.625 + sz]));
    }
    return boxes;
  }
  if (name === 'hopper') {
    const boxes = hopperBoxes().map((b) => boxFrom(state, b, autoCull(b)));
    const f = getFacing(state);
    if (f !== Dir.DOWN) {
      const [ox, oz] = alongFacing(f, 0.4375);
      boxes.push(boxFrom(state, [0.375 + ox, 0.25, 0.375 + oz, 0.625 + ox, 0.4375, 0.625 + oz]));
    } else {
      boxes.push(boxFrom(state, [0.375, 0.0625, 0.375, 0.625, 0.25, 0.625]));
    }
    return boxes;
  }
  if (name === 'chest' || name === 'trapped_chest') {
    return [boxFrom(state, [0.0625, 0, 0.0625, 0.9375, 0.875, 0.9375])];
  }
  if (name === 'piston' || name === 'sticky_piston') {
    if (!getBool(state, 'extended')) return [boxFrom(state, FULL_CUBE, CULL_ALL)];
    const b = pistonBaseBox(getFacing(state));
    return [boxFrom(state, b, autoCull(b))];
  }
  if (name === 'piston_head') {
    const f = getFacing(state);
    const head = pistonHeadBox(f);
    const rod = thinAxisBox(f, 0.375, 0.625);
    // Trim the rod so it does not z-fight with the head.
    return [boxFrom(state, head, autoCull(head)), boxFrom(state, rod, 0)];
  }
  if (name === 'lantern') {
    return getBool(state, 'hanging')
      ? [boxFrom(state, [0.3125, 0.125, 0.3125, 0.6875, 0.75, 0.6875])]
      : [boxFrom(state, [0.3125, 0, 0.3125, 0.6875, 0.5625, 0.6875])];
  }
  if (name === 'campfire') {
    return [
      boxFrom(state, [0, 0, 0, 1, 0.0625, 1], 1 << Dir.DOWN),
      boxFrom(state, [0.0625, 0.0625, 0.0625, 0.9375, 0.375, 0.9375]),
    ];
  }
  if (name === 'end_rod') {
    const f = getFacing(state);
    return [boxFrom(state, thinAxisBox(f, 0.375, 0.625))];
  }
  if (name === 'daylight_detector') {
    return [boxFrom(state, [0, 0, 0, 1, 0.375, 1], 1 << Dir.DOWN)];
  }
  if (name === 'snow_layer') {
    const layers = getInt(state, 'layers');
    const b: Aabb = [0, 0, 0, 1, layers / 8, 1];
    return [boxFrom(state, b, autoCull(b))];
  }
  if (name === 'farmland') {
    return [boxFrom(state, [0, 0, 0, 1, 0.9375, 1], 1 << Dir.DOWN | 1 << Dir.NORTH | 1 << Dir.SOUTH | 1 << Dir.WEST | 1 << Dir.EAST)];
  }
  if (name === 'cactus') {
    return [boxFrom(state, [0.0625, 0, 0.0625, 0.9375, 1, 0.9375], (1 << Dir.DOWN) | (1 << Dir.UP))];
  }
  if (name === 'end_portal_frame') {
    const boxes = [boxFrom(state, [0, 0, 0, 1, 0.8125, 1], autoCull([0, 0, 0, 1, 0.8125, 1]))];
    if (getBool(state, 'eye')) boxes.push(boxFrom(state, [0.25, 0.8125, 0.25, 0.75, 1, 0.75]));
    return boxes;
  }
  if (name === 'dragon_egg') {
    return [boxFrom(state, [0.0625, 0, 0.0625, 0.9375, 1, 0.9375])];
  }
  if (name === 'end_portal') {
    return [boxFrom(state, [0, 0, 0, 1, 0.75, 1])];
  }
  if (name === 'nether_portal') {
    const axis = getProp(state, 'axis');
    return axis === 'x'
      ? [boxFrom(state, [0, 0, 0.375, 1, 1, 0.625])]
      : [boxFrom(state, [0.375, 0, 0, 0.625, 1, 1])];
  }
  if (name === 'chorus_plant') {
    return fenceBoxes(state, 0.1875, 0.8125, 1).map((b) => boxFrom(state, b, autoCull(b)));
  }
  if (name.endsWith('_fence')) {
    // A fence is a post plus two thin rails per connected side. Its collision
    // box is the simple full-height version above; only the visible geometry
    // is split, exactly as in vanilla.
    const boxes: RenderBox[] = [boxFrom(state, [0.375, 0, 0.375, 0.625, 1, 0.625])];
    const rails: [number, number][] = [[0.375, 0.5625], [0.75, 0.9375]];
    const sides: [string, Aabb][] = [
      ['north', [0.4375, 0, 0, 0.5625, 0, 0.375]],
      ['south', [0.4375, 0, 0.625, 0.5625, 0, 1]],
      ['west', [0, 0, 0.4375, 0.375, 0, 0.5625]],
      ['east', [0.625, 0, 0.4375, 1, 0, 0.5625]],
    ];
    for (const [prop, span] of sides) {
      if (!getBool(state, prop)) continue;
      for (const [y0, y1] of rails) {
        boxes.push(boxFrom(state, [span[0], y0, span[2], span[3], y1, span[5]]));
      }
    }
    return boxes;
  }
  if (name === 'moving_piston') return [];

  // Generic shapes fall back to the collision boxes, which are the same solid.
  const boxes = buildCollision(state);
  if (boxes.length === 0) return [];
  return boxes.map((b) => boxFrom(state, b, autoCull(b)));
}

/** Offset components for moving `distance` along a horizontal facing. */
function alongFacing(f: Direction, distance: number): [number, number] {
  switch (f) {
    case Dir.NORTH: return [0, -distance];
    case Dir.SOUTH: return [0, distance];
    case Dir.WEST: return [-distance, 0];
    default: return [distance, 0];
  }
}
